import React, { useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, Calendar, ChevronLeft, ChevronRight, Eye, Filter, FileText } from 'lucide-react';
import { format, parseISO, subDays, startOfMonth, isWithinInterval } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import clsx from 'clsx';
import { useHistoryConversations } from '../../hooks/whatsapp/useChat';
import ConversationViewModal from './ConversationViewModal';
import { formatPhone, parseUTC } from '../../services/whatsapp/waFormatters';
import { PRIORITIES } from '../../services/whatsapp/waConstants';

const STATUS_OPTS = [
  { key: 'all', label: 'Todos' },
  { key: 'open', label: 'Abertos' },
  { key: 'waiting', label: 'Aguardando' },
  { key: 'closed', label: 'Encerrados' },
];

const PRIORITY_OPTS = [
  { key: 'all', label: 'Todas' },
  { key: 'urgent', label: 'Urgente' },
  { key: 'high', label: 'Alta' },
  { key: 'medium', label: 'Media' },
  { key: 'low', label: 'Baixa' },
];

function generateProtocol(id: string, date?: string): string {
  const d = date ? parseISO(date) : null;
  const year = d && !Number.isNaN(d.getTime()) ? d.getFullYear() : new Date().getFullYear();
  const hash = (id || '')
    .replace(/[^0-9]/g, '')
    .slice(0, 5)
    .padStart(5, '0');
  return `ATD-${year}-${hash}`;
}

export default function WaHistoryPage() {
  // Inclui conversas de departamentos nao permitidos — ja vem mascaradas do
  // servidor (redacted), nunca com o dado real (issue #43).
  const { data: conversations } = useHistoryConversations();
  const [searchParams] = useSearchParams();
  const [selectedConv, setSelectedConv] = useState<(typeof conversations)[0] | null>(null);
  // Pré-filtra pelo cliente quando aberto pelo botão "Ver histórico" do chat
  // (issue #31): ?cliente=<telefone>. Lido só uma vez no mount; o usuário pode
  // limpar/alterar a busca livremente depois.
  const [search, setSearch] = useState(() => searchParams.get('cliente') || '');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [period, setPeriod] = useState<'7d' | '30d' | 'mes' | 'custom'>('mes');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [page, setPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const perPage = 15;

  const dateRange = useMemo(() => {
    const now = new Date();
    if (period === '7d') return { start: subDays(now, 7), end: now };
    if (period === '30d') return { start: subDays(now, 30), end: now };
    if (period === 'mes') return { start: startOfMonth(now), end: now };
    if (period === 'custom' && customStart && customEnd)
      return { start: parseISO(customStart), end: parseISO(customEnd) };
    return null;
  }, [period, customStart, customEnd]);

  const filtered = useMemo(() => {
    return conversations
      .filter(c => {
        if (search) {
          const q = search.toLowerCase();
          const protocol = generateProtocol(c.id, c.started_at).toLowerCase();
          if (
            !c.client_name.toLowerCase().includes(q) &&
            !c.client_phone.includes(q) &&
            !(c.subject || '').toLowerCase().includes(q) &&
            !(c.assigned_agent_name || '').toLowerCase().includes(q) &&
            !protocol.includes(q)
          )
            return false;
        }
        if (statusFilter !== 'all' && c.status !== statusFilter) return false;
        if (priorityFilter !== 'all' && c.priority !== priorityFilter) return false;
        if (dateRange) {
          // started_at vem em UTC do backend — parseUTC p/ comparar no instante certo.
          const d = c.started_at ? parseUTC(c.started_at) : null;
          if (d && !isWithinInterval(d, { start: dateRange.start, end: dateRange.end }))
            return false;
        }
        return true;
      })
      .sort((a, b) => new Date(b.started_at).getTime() - new Date(a.started_at).getTime());
  }, [conversations, search, statusFilter, priorityFilter, dateRange]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const paged = filtered.slice((page - 1) * perPage, page * perPage);

  const handleOpenConversation = (conv: (typeof conversations)[0]) => {
    setSelectedConv(conv);
  };

  const statusBadge = (status: string) => {
    const map: Record<string, string> = {
      open: 'bg-blue-100 text-blue-700',
      waiting: 'bg-orange-100 text-orange-700',
      closed: 'bg-gray-100 text-gray-600',
    };
    const labels: Record<string, string> = {
      open: 'Aberto',
      waiting: 'Aguardando',
      closed: 'Encerrado',
    };
    return (
      <span
        className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${map[status] || map.open}`}
      >
        {labels[status] || status}
      </span>
    );
  };

  const priorityDot = (priority: string) => {
    const colors: Record<string, string> = {
      low: 'bg-gray-400',
      medium: 'bg-blue-500',
      high: 'bg-orange-500',
      urgent: 'bg-red-500 animate-pulse',
    };
    return (
      <span className={`inline-block h-2 w-2 rounded-full ${colors[priority] || colors.medium}`} />
    );
  };

  return (
    <div className="min-h-screen space-y-5 bg-[#f5f5f7] p-4 sm:p-6 lg:p-8">
      {/* Header */}
      <div className="animate-fade-in flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <h1 className="text-gradient text-2xl font-bold sm:text-3xl">Historico</h1>
          <p className="mt-1 text-sm text-gray-500">{filtered.length} conversas encontradas</p>
        </div>
        <button
          type="button"
          onClick={() => setShowFilters(!showFilters)}
          className={clsx(
            'flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-medium shadow-wa-apple transition-all',
            showFilters ? 'bg-gray-800 text-white' : 'hover-lift bg-white text-gray-700',
          )}
        >
          <Filter size={16} />
          Filtros
        </button>
      </div>

      {/* Search */}
      <div className="animate-slide-up relative max-w-lg">
        <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          placeholder="Buscar por nome, telefone, protocolo, assunto ou atendente..."
          value={search}
          onChange={e => {
            setSearch(e.target.value);
            setPage(1);
          }}
          className="w-full rounded-xl bg-white py-2.5 pl-10 pr-4 text-sm shadow-wa-apple focus:outline-none focus:ring-2 focus:ring-wa-500/50"
        />
      </div>

      {/* Filters panel */}
      {showFilters && (
        <div className="animate-slide-up space-y-4 rounded-2xl bg-white p-5 shadow-wa-apple">
          {/* Periodo */}
          <div>
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-gray-400">
              Periodo
            </label>
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex items-center overflow-hidden rounded-xl bg-gray-50">
                {(
                  [
                    ['7d', '7 dias'],
                    ['30d', '30 dias'],
                    ['mes', 'Este mes'],
                    ['custom', 'Personalizado'],
                  ] as const
                ).map(([key, label]) => (
                  <button
                    type="button"
                    key={key}
                    onClick={() => {
                      setPeriod(key);
                      setPage(1);
                    }}
                    className={`px-3 py-2 text-xs transition-colors ${period === key ? 'bg-gray-800 font-semibold text-white' : 'text-gray-600 hover:bg-gray-100'}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              {period === 'custom' && (
                <div className="flex items-center gap-2">
                  <Calendar size={14} className="text-gray-400" />
                  <input
                    type="date"
                    value={customStart}
                    onChange={e => setCustomStart(e.target.value)}
                    className="rounded-lg bg-gray-50 px-2 py-1.5 text-xs text-gray-700 outline-none"
                  />
                  <span className="text-gray-300">—</span>
                  <input
                    type="date"
                    value={customEnd}
                    onChange={e => setCustomEnd(e.target.value)}
                    className="rounded-lg bg-gray-50 px-2 py-1.5 text-xs text-gray-700 outline-none"
                  />
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {/* Status */}
            <div>
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-gray-400">
                Status
              </label>
              <div className="flex flex-wrap gap-1.5">
                {STATUS_OPTS.map(opt => (
                  <button
                    type="button"
                    key={opt.key}
                    onClick={() => {
                      setStatusFilter(opt.key);
                      setPage(1);
                    }}
                    className={clsx(
                      'rounded-full px-3 py-1.5 text-xs font-medium transition-all',
                      statusFilter === opt.key
                        ? 'bg-gray-800 text-white'
                        : 'bg-gray-50 text-gray-500 hover:bg-gray-100',
                    )}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Prioridade */}
            <div>
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-gray-400">
                Prioridade
              </label>
              <div className="flex flex-wrap gap-1.5">
                {PRIORITY_OPTS.map(opt => (
                  <button
                    type="button"
                    key={opt.key}
                    onClick={() => {
                      setPriorityFilter(opt.key);
                      setPage(1);
                    }}
                    className={clsx(
                      'rounded-full px-3 py-1.5 text-xs font-medium transition-all',
                      priorityFilter === opt.key
                        ? 'bg-gray-800 text-white'
                        : 'bg-gray-50 text-gray-500 hover:bg-gray-100',
                    )}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="animate-slide-up overflow-hidden rounded-2xl bg-white shadow-wa-apple">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200/60">
                <th className="px-5 py-3 text-left text-xs font-semibold uppercase text-gray-500">
                  Protocolo
                </th>
                <th className="px-5 py-3 text-left text-xs font-semibold uppercase text-gray-500">
                  Cliente
                </th>
                <th className="hidden px-5 py-3 text-left text-xs font-semibold uppercase text-gray-500 md:table-cell">
                  Assunto
                </th>
                <th className="px-5 py-3 text-left text-xs font-semibold uppercase text-gray-500">
                  Status
                </th>
                <th className="hidden px-5 py-3 text-left text-xs font-semibold uppercase text-gray-500 lg:table-cell">
                  Prioridade
                </th>
                <th className="hidden px-5 py-3 text-left text-xs font-semibold uppercase text-gray-500 lg:table-cell">
                  Atendente
                </th>
                <th className="hidden px-5 py-3 text-left text-xs font-semibold uppercase text-gray-500 sm:table-cell">
                  Data
                </th>
                {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
                <th className="px-5 py-3 text-left text-xs font-semibold uppercase text-gray-500" />
              </tr>
            </thead>
            <tbody>
              {paged.map(conv => {
                const protocol = generateProtocol(conv.id, conv.started_at);
                // Conversa redigida (depto nao permitido): os dados sensiveis ja
                // vem mascarados do servidor. O blur abaixo e so estetico.
                const blurCls = conv.redacted ? 'select-none italic text-gray-400 blur-[2px]' : '';
                return (
                  <tr
                    key={conv.id}
                    className="border-b border-gray-100/60 transition-colors hover:bg-gray-50/50"
                  >
                    <td className="px-5 py-3">
                      <span className="rounded bg-wa-50 px-2 py-0.5 font-mono text-xs font-semibold text-wa-600">
                        {protocol}
                      </span>
                    </td>
                    <td className="px-5 py-3">
                      <div className={blurCls}>
                        <p className="text-sm font-medium text-gray-900">{conv.client_name}</p>
                        <p className="text-[11px] text-gray-400">
                          {formatPhone(conv.client_phone)}
                        </p>
                      </div>
                    </td>
                    <td className="hidden px-5 py-3 md:table-cell">
                      <p className={`max-w-[180px] truncate text-gray-600 ${blurCls}`}>
                        {conv.subject || '—'}
                      </p>
                    </td>
                    <td className="px-5 py-3">{statusBadge(conv.status)}</td>
                    <td className="hidden px-5 py-3 lg:table-cell">
                      <div className="flex items-center gap-1.5">
                        {priorityDot(conv.priority)}
                        <span className="text-xs text-gray-600">
                          {PRIORITIES[conv.priority]?.label || conv.priority}
                        </span>
                      </div>
                    </td>
                    <td className="hidden px-5 py-3 text-gray-600 lg:table-cell">
                      <span className={blurCls}>{conv.assigned_agent_name || '—'}</span>
                    </td>
                    <td className="hidden px-5 py-3 text-xs text-gray-500 sm:table-cell">
                      {conv.started_at
                        ? format(parseUTC(conv.started_at), 'dd/MM/yy HH:mm', { locale: ptBR })
                        : '—'}
                    </td>
                    <td className="px-5 py-3">
                      {conv.redacted ? (
                        <span className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold text-gray-300">
                          <Eye size={13} />
                          Restrito
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => handleOpenConversation(conv)}
                          className="flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs font-semibold text-wa-600 transition-colors hover:bg-wa-50"
                        >
                          <Eye size={13} />
                          Abrir
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {paged.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-5 py-12 text-center text-gray-400">
                    <FileText size={32} className="mx-auto mb-2 text-gray-300" />
                    <p className="text-sm">Nenhuma conversa encontrada</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between border-t border-gray-100/60 px-5 py-3">
          <p className="text-xs text-gray-500">
            {filtered.length > 0
              ? `${(page - 1) * perPage + 1}-${Math.min(page * perPage, filtered.length)} de ${filtered.length}`
              : '0 resultados'}
          </p>
          <div className="flex items-center gap-1">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => setPage(p => p - 1)}
              className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 disabled:opacity-40"
            >
              <ChevronLeft size={16} />
            </button>
            <span className="px-2 text-xs text-gray-600">
              {page} / {totalPages}
            </span>
            <button
              type="button"
              disabled={page >= totalPages}
              onClick={() => setPage(p => p + 1)}
              className="rounded-lg p-1.5 text-gray-600 hover:bg-gray-100 disabled:opacity-40"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile cards */}
      <div className="space-y-3 sm:hidden">
        {paged.map(conv => {
          const protocol = generateProtocol(conv.id, conv.started_at);
          const blurCls = conv.redacted ? 'select-none italic text-gray-400 blur-[2px]' : '';
          return (
            // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
            <div
              key={conv.id}
              onClick={() => !conv.redacted && handleOpenConversation(conv)}
              className={clsx(
                'rounded-2xl bg-white p-4 shadow-wa-apple transition-all',
                conv.redacted ? 'cursor-default' : 'hover-lift cursor-pointer',
              )}
            >
              <div className="mb-2 flex items-center justify-between">
                <span className="rounded bg-wa-50 px-2 py-0.5 font-mono text-[10px] font-semibold text-wa-600">
                  {protocol}
                </span>
                {statusBadge(conv.status)}
              </div>
              <p className={`text-sm font-semibold text-gray-900 ${blurCls}`}>{conv.client_name}</p>
              <p className={`mt-0.5 text-xs text-gray-500 ${blurCls}`}>
                {conv.subject || formatPhone(conv.client_phone)}
              </p>
              <div className="mt-2 flex items-center justify-between text-[11px] text-gray-400">
                <span className={blurCls}>{conv.assigned_agent_name || 'Nao atribuido'}</span>
                <span>
                  {conv.started_at ? format(parseUTC(conv.started_at), 'dd/MM HH:mm') : '—'}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Modal de visualizacao */}
      {selectedConv && (
        <ConversationViewModal
          isOpen={!!selectedConv}
          onClose={() => setSelectedConv(null)}
          conversation={selectedConv}
          protocol={generateProtocol(selectedConv.id, selectedConv.started_at)}
        />
      )}
    </div>
  );
}
